/**
 * MobileRedirect — auto-sends LOGGED-IN phone users to /m. Rendered by App.js inside
 * <Router> (before <Routes>); renders null. This is the ONE feature crossing into
 * shared root routing, so it is written to be loop-proof and desktop-safe. Kept in
 * its own module so it can be unit-tested without importing the whole desktop app.
 *
 * Desktop safety: `!isMobileDevice()` early-returns BEFORE any fetch or navigation,
 * so desktop-width users trigger zero extra requests and zero behaviour change.
 *
 * Loop safety:
 *   • sessionStartedAtMobile ref — if the session began on /m (e.g. right after a
 *     redirect's hard reload), never redirect again → no /m→…→/m bounce.
 *   • path.startsWith("/m") early-return — never redirects while already on /m.
 *   • preferDesktop flag (set by the /m "View desktop site" switch) — a user who
 *     chose desktop is never dragged back to /m.
 *   • auth-gated (redirect only when res.ok) — a logged-OUT phone on / gets 403 and
 *     stays on the login page; only after login (SPA nav → a real route) does the
 *     effect re-fire and redirect. No login↔/m loop.
 * The effect keys on location.pathname so the post-login navigation re-triggers the
 * check; every ineligible case short-circuits before the fetch.
 *
 * STORY DEEP LINKS (the exception to all of the above): a phone user who opened the
 * tab on /highlights/<slug> was sent a link to that one story, so /m is the wrong
 * destination. Such a tab is marked once (see utils/storyEntry) and this component
 * then stands down for the rest of the session — whether the visitor needs to log
 * in is StoryGate's call, because only it knows if that particular story is public
 * or gated. The mark is what keeps the journey loop-free: when SignIn returns a
 * newly-logged-in user to the story, that navigation is not mistaken for a fresh
 * "phone on a desktop route" and does not bounce to /m.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { isMobileDevice } from "./utils/mobileDetect";
import { clearStoryEntry, initStoryEntry } from "./utils/storyEntry";

export default function MobileRedirect() {
  const location = useLocation();
  const sessionStartedAtMobile = useRef(window.location.pathname.startsWith("/m"));

  // Read the ENTRY url (not location.pathname, which has already moved on by the
  // time a later render runs). Gated on isMobileDevice() so a desktop visitor to a
  // story never even writes the marker.
  const [cameForStory] = useState(
    () => isMobileDevice() && initStoryEntry(window.location.pathname)
  );

  useEffect(() => {
    if (sessionStartedAtMobile.current) return;
    const path = location.pathname;
    // Reaching /m ends the story journey — drop the marker so a later navigation
    // out of /m goes back to the ordinary rules.
    if (path.startsWith("/m")) { clearStoryEntry(); return; }
    if (path.startsWith("/admin") || path.startsWith("/report-render")) return;
    if (localStorage.getItem("preferDesktop") === "true") return;
    if (!isMobileDevice()) return;

    // Story tab: /m is never the destination, and the auth question belongs to
    // StoryGate. Nothing to do — not even the user-info fetch.
    if (cameForStory) return;

    // Logged-in phone on a non-/m route → send to the mobile app. replace() (not
    // push) so the back button doesn't land on the pre-redirect route.
    fetch("/api/user-info/", { credentials: "include" })
      .then((r) => { if (r.ok) window.location.replace("/m"); })
      .catch(() => {});
  }, [location.pathname, cameForStory]);

  return null;
}
